<script lang="ts">
    import { getContext } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { Validator as IpNumValidator } from 'ip-num'
    import { isEqual } from 'lodash-es'
    import * as yup from 'yup'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { asAny } from 'hydria/svelte_helpers'

    import type { IDomain, IHost } from 'wdom/domain/interfaces'

    import type { DomainHandler } from './domain'

    import MiniKeyboard from 'wdom/components/MiniKeyboard.svelte'

    const Domain = getContext('Domain') as DomainHandler
    const status = sderived(Domain.status, ($status) => $status)

    let domain: IDomain = $derived($status == 'ready' ? Domain.domain : ({} as IDomain))

    const hosts = sderived(Domain.hosts, ($hosts) => $hosts)
    const runningHostIds = sderived(Domain.hostIdnasWithTasks, ($hostIds) => $hostIds)

    interface IHostSimple {
        name_idna: string
        ipv4?: string
        ipv6?: string
        name: string
    }

    // either undefined or a the host to edit.
    // if host.id is null, this will be a new host.
    let hostToEdit: IHostSimple | undefined = $state()
    let hostOriginal: IHostSimple | undefined = $state()
    let hostFormValue: IHostSimple | undefined = $state()

    function makeEdit(inHost: IHostSimple | undefined) {
        hostToEdit = inHost
        if (inHost == null) {
            hostOriginal = undefined
            hostFormValue = undefined
        } else {
            const host = { ...inHost }
            for (const key of ['ipv4', 'ipv6']) {
                if (host[key] == null) host[key] = ''
            }
            hostOriginal = host
            hostFormValue = { ...host }
        }
    }

    const schema = yup.object().shape(
        {
            name: yup.string().trim().required().label('Name'),
            ipv4: yup
                .string()
                .when(['ipv6'], ([ipv6], lschema: yup.StringSchema) => {
                    if (ipv6 == '') return lschema.required()
                    return lschema
                })
                .test('ipv4', '${path} is invalid', (value: any) => {
                    if (value == '' || value == null) return true
                    return IpNumValidator.isValidIPv4String(value)[0]
                })
                .label('ipv4'),
            ipv6: yup
                .string()
                .when(['ipv4'], ([ipv4], lschema: yup.StringSchema) => {
                    if (ipv4 == '') return lschema.required()
                    return lschema
                })
                .test({
                    name: 'ipv6',
                    test: (value: any) => {
                        if (value == '' || value == null) return true
                        return IpNumValidator.isValidIPv6String(value)[0]
                    },
                    message: 'IP invalid',
                })
                .label('ipv6'),
        },
        [['ipv4', 'ipv6']],
    )

    // @ts-expect-error
    let nameIsValid = $derived(hostFormValue != null && yup.reach(schema, 'name').isValidSync(hostFormValue.name))
    let ipv4IsValid: boolean = $derived.by(() => {
        try {
            hostFormValue != null && schema.validateSyncAt('ipv4', hostFormValue)
            return true
        } catch {
            return false
        }
    })

    let ipv6IsValid: boolean = $derived.by(() => {
        try {
            hostFormValue != null && schema.validateSyncAt('ipv6', hostFormValue)
            return true
        } catch {
            return false
        }
    })

    let formIsValid = $derived(hostFormValue != null && schema.isValidSync(hostFormValue))

    // Used by both edit & remove while queuing the task
    let saving = $state(false)

    async function save() {
        if (hostFormValue == null || !formIsValid) return

        saving = true
        try {
            await simpleFetch(hostFormValue.name_idna == null ? 'PUT' : 'POST', `/json/domain/1/${domain.idna}/host` + (hostFormValue.name_idna == null ? '' : `/${hostFormValue.name_idna}`), {
                name: hostFormValue.name,
                ipv4: hostFormValue.ipv4,
                ipv6: hostFormValue.ipv6,
            })

            // if (res.status == 202) {
            hostToEdit = undefined
            // }
        } catch {
            notify({
                title: $_('Custom name server'),
                text: $_('Error updating name server'),
                type: 'danger',
            })
        }
        saving = false
    }

    // if defined, the host to confirm deletion
    let hostToRemove: IHost | undefined = $state()

    async function remove() {
        if (hostToRemove == null) return

        try {
            await simpleFetch('DELETE', `/json/domain/1/${domain.idna}/host/${hostToRemove.name_idna}`)
            // if (res.status == 202) {
            hostToRemove = undefined
            // }
        } catch {
            notify({
                title: $_('Custom name server'),
                text: $_('Error removing name server'),
                type: 'danger',
            })
        }
    }

    $effect(() => {
        if ((hostToEdit || hostToRemove) && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!(hostToEdit || hostToRemove) && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

{#if $status == 'ready'}
    <div class="card card-custom-nameservers">
        <div class="card-title text-primary">{$_('Custom name servers')}</div>
        {#if domain.system == 'webnames'}
            <div class="card-body">
                <p>Non-supporté par l’API de WebNames</p>
            </div>
        {:else if !$hosts.length}
            <div class="card-body">
                <p>{$_('No custom name servers')}</p>
            </div>
        {:else}
            <div class="card-body">
                <ul class="list-group">
                    {#each $hosts as host (host.name_idna)}
                        <li class="list-group-item">
                            {#if $runningHostIds.has(host.name_idna)}
                                <span class="float-end host-progress">
                                    <div class="progress progress-striped active">
                                        <div class="bar" style="width: 100%"></div>
                                    </div>
                                </span>
                            {:else}
                                <span class="btn-group btn-group-sm float-end">
                                    <button
                                        type="button"
                                        class="btn btn-info"
                                        onclick={() => {
                                            makeEdit(host)
                                        }}
                                        disabled={saving}><Icon inline icon="fa:pencil-square-o" /></button
                                    >
                                    <button type="button" class="btn btn-danger" onclick={() => (hostToRemove = host)} disabled={saving}><Icon inline icon="fa:trash-o" /></button>
                                </span>
                            {/if}
                            {host.name}<span class="muted">.{domain.domain}.{domain.tld}</span>
                            {#if host.ipv4 != null}
                                <div>{host.ipv4} <span class="muted">(v4)</span></div>
                            {/if}
                            {#if host.ipv6 != null}
                                <div>{host.ipv6} <span class="muted">(v6)</span></div>
                            {/if}
                        </li>
                    {/each}
                </ul>
            </div>
        {/if}
        <div class="card-footer text-bg-light text-end">
            <button
                type="button"
                class="btn btn-primary"
                onclick={() => {
                    makeEdit(asAny({ name_idna: null, name: '', ipv4: '', ipv6: '' }))
                }}>{$_('Create new name server')}</button
            >
        </div>
    </div>
{/if}

{#if hostToRemove != null && !saving}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }} aria-hidden="true" onclick={() => (hostToRemove = undefined)}>
        <div
            class="modal-dialog"
            onclick={(e) => {
                e.stopPropagation()
            }}
            aria-hidden="true"
        >
            <div class="modal-content">
                <div class="modal-header text-bg-primary"><div>{$_('Remove host')}</div></div>
                <div class="modal-body">{$_('Please confirm the removal of the host, “{name}”', { name: hostToRemove.name })}</div>
                <div class="modal-footer">
                    <button class="btn btn-outline-secondary" type="button" onclick={() => (hostToRemove = undefined)}>{$_('Cancel')}</button>
                    <button
                        class="btn btn-danger"
                        type="button"
                        onclick={() => {
                            remove()
                        }}>{$_('Remove host')}</button
                    >
                </div>
            </div>
        </div>
        <div class="modal-backdrop"></div>
    </div>
{/if}
{#if hostToEdit != null && !saving && hostFormValue}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }} aria-hidden="true" onclick={() => (hostToEdit = undefined)}>
        <div
            class="modal-dialog"
            aria-hidden="true"
            onclick={(e) => {
                e.stopPropagation()
            }}
        >
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>
                        {#if !hostFormValue.name_idna}{$_('Nouveau serveur DNS')}
                        {:else}{$_('Édition d’un serveur DNS')}
                        {/if}
                    </div>
                </div>
                <div class="modal-body">
                    <form class="d-grid gap-2" novalidate>
                        <div class="row">
                            <label for="host-modal-name" class="col-sm-4 col-form-label">{$_('Host name')}</label>
                            <div class="col-sm-8">
                                <div class="input-group">
                                    <input type="text" class="form-control" id="host-modal-name" placeholder="ns1" required bind:value={hostFormValue.name} class:is-invalid={!nameIsValid} />
                                    <span class="input-group-text">.{domain.domain}.{domain.tld}</span>
                                    <MiniKeyboard eid="host-modal-name" modal={true} />
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <label for="host-modal-ipv4" class="col-sm-4 col-form-label">{$_('IP address (v4)')}</label>
                            <div class="col-sm-8">
                                <input type="text" class="form-control" placeholder="xxx.xxx.xxx.xxx" id="host-modal-ipv4" bind:value={hostFormValue.ipv4} class:is-invalid={!ipv4IsValid} />
                            </div>
                        </div>
                        <div class="row">
                            <label for="host-modal-ipv6" class="col-sm-4 col-form-label">{$_('IP address (v6)')}</label>
                            <div class="col-sm-8">
                                <input type="text" class="form-control" placeholder="xxxx:xxxx::xxxx" id="host-modal-ipv6" bind:value={hostFormValue.ipv6} class:is-invalid={!ipv6IsValid} />
                            </div>
                        </div>
                    </form>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-outline-secondary" type="button" onclick={() => (hostToEdit = undefined)}>{$_('Cancel')}</button>
                    <button
                        class="btn btn-primary"
                        type="button"
                        disabled={!formIsValid || isEqual(hostOriginal, hostFormValue)}
                        onclick={() => {
                            save()
                        }}>{$_('Save')}</button
                    >
                </div>
            </div>
        </div>
        <div class="modal-backdrop"></div>
    </div>
{/if}
